import React, { useEffect, useState } from "react";
import { Download, Smartphone, X, Share, Plus, Check } from "lucide-react";
import Logo from "./Logo";

/**
 * Bouton + modal pour installer FB Environnement comme application :
 * - Android / Chrome Desktop : capture beforeinstallprompt → install natif
 * - iOS Safari : guide visuel "Partager → Sur l'écran d'accueil"
 * - Si déjà installée (display-mode: standalone) : se masque
 */
export default function InstallAppButton({ variant = "floating" }) {
  const [deferred, setDeferred] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Detect installed state
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setShowModal(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (isStandalone || installed) return null;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/Android/i.test(ua);
  const isAndroid = /Android/i.test(ua);

  const handleClick = async () => {
    if (deferred) {
      // Native prompt path (Android Chrome, Desktop Chrome/Edge)
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") setInstalled(true);
      setDeferred(null);
      return;
    }
    // No native prompt — show modal with manual instructions (typical iOS Safari)
    setShowModal(true);
  };

  const floating = (
    <button
      type="button"
      onClick={handleClick}
      data-testid="install-app-btn"
      className="group fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 bg-[#1F6B2E] hover:bg-[#145020] text-white font-bold px-4 py-3 rounded-full shadow-xl shadow-[#1F6B2E]/30 transition-all active:scale-95"
      aria-label="Installer l'application FB Environnement"
    >
      <span className="relative grid place-items-center w-6 h-6 rounded-full bg-[#9DCB2E] text-[#0f3a18]">
        <Download className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
      <span className="text-sm">Installer l&apos;app</span>
    </button>
  );

  const topbar = (
    <button
      type="button"
      onClick={handleClick}
      data-testid="install-app-btn-topbar"
      className="inline-flex items-center gap-1.5 bg-[#9DCB2E] text-[#0f3a18] hover:bg-white hover:text-[#0f3a18] font-black px-2.5 py-1 rounded-full text-[11px] transition-colors uppercase tracking-wider"
      aria-label="Installer l'application FB Environnement"
    >
      <Download className="w-3 h-3" strokeWidth={3} />
      Installer l&apos;app
    </button>
  );

  const inline = (
    <button
      type="button"
      onClick={handleClick}
      data-testid="install-app-btn-inline"
      className="inline-flex items-center gap-2 bg-[#1F6B2E] hover:bg-[#145020] text-white font-bold px-4 py-2.5 rounded-full text-sm transition-all active:scale-95 shadow-md shadow-[#1F6B2E]/30"
    >
      <Download className="w-4 h-4" strokeWidth={2.8} />
      Installer l&apos;application
    </button>
  );

  const node = variant === "topbar" ? topbar : variant === "inline" ? inline : floating;

  return (
    <>
      {node}
      {showModal && (
        <InstallModal isIOS={isIOS} isAndroid={isAndroid} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}

function InstallModal({ isIOS, isAndroid, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="install-app-modal"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center px-4 py-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#1F6B2E] text-white px-5 py-5 flex items-center gap-4 relative">
          <div className="bg-white p-2 rounded-2xl">
            <Logo size={42} />
          </div>
          <div className="flex-1 leading-tight">
            <div className="font-heading font-black text-lg">FB Environnement</div>
            <div className="text-xs opacity-90">Installez l&apos;application sur votre téléphone</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="install-modal-close"
            aria-label="Fermer"
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {isIOS ? (
            <IOSInstructions />
          ) : isAndroid ? (
            <AndroidInstructions />
          ) : (
            <>
              <DesktopInstructions />
              <div className="text-xs text-stone-500 border-t border-stone-100 pt-3">
                Sur iPhone, ouvrez ce site dans Safari, puis suivez les étapes iOS ci-dessous.
              </div>
              <IOSInstructions compact />
            </>
          )}

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 text-xs text-emerald-900 flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3} />
            <span>
              Une fois installée, l&apos;app s&apos;ouvre directement comme une vraie application :
              accès rapide au tarif, contact, et nos coordonnées hors-ligne.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function IOSInstructions({ compact = false }) {
  return (
    <div className={compact ? "" : "space-y-3"}>
      {!compact && (
        <div className="inline-flex items-center gap-2 text-sm font-black text-stone-800">
          <Smartphone className="w-4 h-4 text-[#1F6B2E]" strokeWidth={2.8} /> iPhone / iPad — Safari
        </div>
      )}
      <ol className="space-y-2.5 text-sm text-stone-700 list-decimal list-inside">
        <li>
          Touchez le bouton
          <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 bg-stone-100 rounded-md font-bold text-stone-900">
            <Share className="w-3.5 h-3.5" strokeWidth={2.5} /> Partager
          </span>
          en bas de Safari.
        </li>
        <li>
          Faites défiler et touchez
          <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 bg-stone-100 rounded-md font-bold text-stone-900">
            <Plus className="w-3.5 h-3.5" strokeWidth={2.8} /> Sur l&apos;écran d&apos;accueil
          </span>
          .
        </li>
        <li>Validez « Ajouter ». L&apos;icône FB Environnement apparaît sur votre écran d&apos;accueil.</li>
      </ol>
    </div>
  );
}

function AndroidInstructions() {
  return (
    <div className="space-y-3">
      <div className="inline-flex items-center gap-2 text-sm font-black text-stone-800">
        <Smartphone className="w-4 h-4 text-[#1F6B2E]" strokeWidth={2.8} /> Android — Chrome
      </div>
      <ol className="space-y-2.5 text-sm text-stone-700 list-decimal list-inside">
        <li>Touchez le menu <strong>⋮</strong> en haut à droite de Chrome.</li>
        <li>
          Choisissez
          <span className="inline-block mx-1 px-2 py-0.5 bg-stone-100 rounded-md font-bold text-stone-900">
            Installer l&apos;application
          </span>
          (ou « Ajouter à l&apos;écran d&apos;accueil »).
        </li>
        <li>Validez. L&apos;icône FB Environnement apparaît sur votre écran d&apos;accueil.</li>
      </ol>
    </div>
  );
}

function DesktopInstructions() {
  return (
    <div className="space-y-3">
      <div className="inline-flex items-center gap-2 text-sm font-black text-stone-800">
        <Download className="w-4 h-4 text-[#1F6B2E]" strokeWidth={2.8} /> Ordinateur — Chrome / Edge
      </div>
      <ol className="space-y-2.5 text-sm text-stone-700 list-decimal list-inside">
        <li>
          Cliquez sur l&apos;icône
          <span className="inline-block mx-1 px-2 py-0.5 bg-stone-100 rounded-md font-bold text-stone-900">
            Installer
          </span>
          dans la barre d&apos;adresse (à droite de l&apos;URL).
        </li>
        <li>Validez. FB Environnement devient une application sur votre bureau.</li>
      </ol>
    </div>
  );
}
